/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  CollisionDetection,
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { Capacitor } from '@capacitor/core'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { GlassButton, GlassCard, GlassPanel } from '@components/common/Glass'
import RankedItemCard from '@components/common/RankedItemCard'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Alert, { AlertInput } from '@components/common/Alert'
import useWaterfallIntro from 'src/hooks/useWaterfallIntro'
import {
  altColor,
  altColorDarker,
  altColorDarkText,
  altColorLighter,
  mainColor,
  mainColorDarker,
  mainColorLighter,
  mainColorMediumText,
} from 'src/ui/theme/tokens'
import Chip from '@mui/material/Chip'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'

const triviaSubmitStyle = {
  '&:not(.Mui-disabled)': {
    background: '#193b58',
    color: '#fff',
    borderColor: '#193b58',
    boxShadow: '0 4px 0 #0e2a40, inset 0 2px 0 #335770',
    '&:hover': { background: '#193b58' },
  },
}

type Item = {
  title: string
  metric?: string
  index: number
  points: number
}

type SoloItem = Item & {
  id: string
  locked?: boolean
  status?: 'correct' | 'unchecked'
  justAdded?: boolean
}

type HintDirection = 'too-high' | 'too-low' | 'correct'

type SoloRankedGameProps = {
  items: Item[]
  maxGuesses: number
  fullScreen: boolean
  gameKey: number
  title: string
  contentType?: 'trivia' | 'poll'
  loading?: boolean
  refreshLabel?: string
  sounds?: {
    click?: () => void
    correct?: () => void
    miss?: () => void
    submit?: () => void
    drag?: () => void
    win?: () => void
    loss?: () => void
  }
  evaluateGuess?: (order: string[], requestId: string) => Promise<{ lockedIds: string[]; guessCount: number }>
  onRefresh?: () => void
  onExit?: () => void
  onGuessCountChange: (count: number) => void
  onGameOver: (result: 'win' | 'loss', guessCount: number, lastGuessOrder: string[]) => void
  resumeState?: {
    guessItems: SoloItem[]
    guessCount: number
    guessHistoryById: Record<string, number[]>
    lastGuessCorrectById: Record<string, boolean>
    lastGuessOrder: string[]
    result?: 'active' | 'win' | 'loss'
  }
  onStateChange?: (
    state: {
      guessItems: SoloItem[]
      guessCount: number
      guessHistoryById: Record<string, number[]>
      lastGuessCorrectById: Record<string, boolean>
      lastGuessOrder: string[]
      result: 'active' | 'win' | 'loss'
    } | null
  ) => void
}

const triggerHaptic = async (style: 'light' | 'medium' | 'success' | 'warning') => {
  const vibrate = (pattern: number | number[]) => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(pattern)
    }
  }

  if (!Capacitor.isNativePlatform()) {
    if (style === 'success') vibrate([20, 40, 20])
    else if (style === 'warning') vibrate([40, 60, 40])
    else if (style === 'medium') vibrate(20)
    else vibrate(10)
    return
  }
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics')
    if (style === 'success') {
      await Haptics.notification({ type: NotificationType.Success })
      return
    }
    if (style === 'warning') {
      await Haptics.notification({ type: NotificationType.Warning })
      return
    }
    const impact = style === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light
    await Haptics.impact({ style: impact })
  } catch {
    // no-op
  }
}

const shuffle = <T,>(list: T[]): T[] => {
  const items = list.slice()
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))

    const ii = items[i]
    const ij = items[j]
    if (ij && ii) {
      ;[items[i], items[j]] = [ij, ii]
    }
  }
  return items
}

const getItemId = (item: Item): string => `${item.index}-${item.title}`

const SoloItemCard = ({
  item,
  index,
  isDragging,
  isOverlay,
  history,
  dragHandle,
  action,
  fullScreen,
  isSwapSelected,
  swapControl,
  isSwapHovered,
  loading,
  disabled,
}: {
  item: SoloItem
  index: number
  isDragging?: boolean
  isOverlay?: boolean
  history?: number[]
  dragHandle?: React.ReactNode
  action?: React.ReactNode
  fullScreen: boolean
  isSwapSelected?: boolean
  swapControl?: React.ReactNode
  isSwapHovered?: boolean
  loading?: boolean
  disabled?: boolean
}) => {
  const showMetric = item.locked
  const tone = item.locked ? 'correct' : 'neutral'
  const swapActiveStyles =
    isSwapSelected || isSwapHovered
      ? {
          borderColor: 'rgba(83,217,245,0.65)',
          boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
          outline: '1.5px solid rgba(83,217,245,0.55)',
          outlineOffset: '0px',
          transform: 'scale(1.012)',
          animation: 'swapFloat 2.4s ease-in-out infinite',
          '@keyframes swapFloat': {
            '0%': {
              transform: 'translateY(0) scale(1.012)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
            '50%': {
              transform: 'translateY(-2px) scale(1.016)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
            '100%': {
              transform: 'translateY(0) scale(1.012)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
            },
          },
        }
      : undefined
  return (
    <RankedItemCard
      index={index + 1}
      title={item.title}
      metric={showMetric ? item.metric : undefined}
      tone={tone}
      fullScreen={fullScreen}
      isDragging={isDragging}
      isOverlay={isOverlay}
      className="swap-card"
      cardSx={{
        ...(swapActiveStyles || {}),
        ...(disabled
          ? {
              opacity: 0.65,
              filter: 'saturate(0.7)',
            }
          : {}),
        ...(isOverlay
          ? {
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
              transform: 'scale(1.01)',
            }
          : {}),
      }}
      loading={!!loading}
      rightAdornment={
        <>
          {action ? <Box>{action}</Box> : null}
          {swapControl ? <Box>{swapControl}</Box> : null}
          {!swapControl && dragHandle ? <Box>{dragHandle}</Box> : null}
        </>
      }
      meta={
        history && history.length ? (
          <Typography
            variant="body2"
            color="#9ca3af"
            sx={{
              textDecoration: 'line-through',
              textDecorationColor: '#666',
              textDecorationThickness: '1px',
              fontWeight: 600,
            }}
            noWrap
          >
            {history.join(', ')}
          </Typography>
        ) : undefined
      }
    />
  )
}

const SortableSoloItem = ({
  item,
  index,
  dragDisabled,
  clickDisabled,
  isDropDisabled,
  history,
  action,
  fullScreen,
  isSwapSelected,
  onSwapSelect,
  swapControl,
  onSwapHover,
  isSwapAnimating,
  isSwapHovered,
  swapAnimationDirection,
  swapPhase,
  swapModeActive,
  loading,
  disabled,
  introOpacity,
  introTransform,
  suppressTransition,
}: {
  item: SoloItem
  index: number
  dragDisabled: boolean
  clickDisabled: boolean
  isDropDisabled: boolean
  history?: number[]
  action?: React.ReactNode
  fullScreen: boolean
  isSwapSelected?: boolean
  onSwapSelect?: () => void
  swapControl?: React.ReactNode
  onSwapHover?: (id: string | null) => void
  isSwapAnimating?: boolean
  isSwapHovered?: boolean
  swapAnimationDirection?: 'up' | 'down'
  swapPhase?: 'out' | 'in' | null
  swapModeActive?: boolean
  loading?: boolean
  disabled?: boolean
  introOpacity?: number
  introTransform?: string
  suppressTransition?: boolean
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: dragDisabled,
    data: {
      locked: item.locked,
    },
  })
  const appliedTransition = item.locked ? 'transform 0ms' : transition
  const baseTransform = CSS.Transform.toString(transform)
  const effectiveTransform = isDragging
    ? baseTransform
    : [baseTransform, introTransform].filter(Boolean).join(' ')
  const effectiveOpacity = isDragging ? 1 : (introOpacity ?? 1)
  const transitionParts =
    isDragging || suppressTransition
      ? []
      : [appliedTransition, 'opacity 220ms ease', 'transform 220ms ease'].filter(Boolean)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isPlaceholder = (item as any).isDragPlaceholder

  return (
    <Box
      ref={setNodeRef}
      onClick={clickDisabled ? undefined : onSwapSelect}
      onPointerEnter={() => onSwapHover?.(item.id)}
      onPointerLeave={() => onSwapHover?.(null)}
      style={{
        transform: effectiveTransform,
        transition: transitionParts.join(', '),
        opacity: isPlaceholder ? 0 : effectiveOpacity,
      }}
      sx={{
        touchAction: 'pan-y',
        cursor: clickDisabled ? 'default' : 'pointer',
        opacity: isDragging ? 1 : 1,
        position: 'relative',
        zIndex: isDragging ? 5 : 'auto',
        filter: isDropDisabled ? 'saturate(0.7)' : 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        animation: item.justAdded && !isDragging ? 'guessSlideIn 260ms ease-out' : undefined,
        ...(isSwapAnimating
          ? {
              zIndex: 10,
              animation:
                swapAnimationDirection === 'down'
                  ? swapPhase === 'out'
                    ? 'swapDownOut 160ms ease-in'
                    : 'swapDownIn 220ms ease-out'
                  : swapPhase === 'out'
                    ? 'swapUpOut 160ms ease-in'
                    : 'swapUpIn 220ms ease-out',
            }
          : {}),
        ...(swapModeActive
          ? {
              '&:hover .swap-card': {
                borderColor: 'rgba(83,217,245,0.65)',
                boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                outline: '1.5px solid rgba(83,217,245,0.55)',
                outlineOffset: '0px',
                transform: 'scale(1.02)',
                animation: 'swapFloat 2.4s ease-in-out infinite',
              },
            }
          : {}),
        '@keyframes guessSlideIn': {
          '0%': { opacity: 0, transform: 'translateY(8px) scale(1)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1.02)' },
        },
        '@keyframes swapDownOut': {
          '0%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          '100%': { opacity: 0, transform: 'translateY(60px) scale(1.02)' },
        },
        '@keyframes swapDownIn': {
          '0%': { opacity: 0, transform: 'translateY(-60px) scale(1.02)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@keyframes swapUpOut': {
          '0%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          '100%': { opacity: 0, transform: 'translateY(-60px) scale(1.02)' },
        },
        '@keyframes swapUpIn': {
          '0%': { opacity: 0, transform: 'translateY(60px) scale(1.02)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
      }}
    >
      <SoloItemCard
        item={item}
        index={index}
        isDragging={isDragging}
        // isDropDisabled={isDropDisabled}
        history={history}
        action={action}
        fullScreen={fullScreen}
        isSwapSelected={isSwapSelected}
        swapControl={swapControl}
        dragHandle={
          <Box
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 54,
              height: 54,
              borderRadius: '8px',
              color: '#9ca3af',
              cursor: dragDisabled ? 'default' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        }
        loading={loading}
      />
    </Box>
  )
}

export default function SoloRankedGame({
  items,
  maxGuesses,
  fullScreen,
  gameKey,
  title,
  contentType = 'trivia',
  loading = false,
  sounds,
  refreshLabel,
  onRefresh,
  onExit,
  onGuessCountChange,
  onGameOver,
  evaluateGuess,
  resumeState,
  onStateChange,
}: SoloRankedGameProps): JSX.Element {
  const [guessItems, setGuessItems] = useState<SoloItem[]>([])
  const [guessCount, setGuessCount] = useState(0)
  const [evaluating, setEvaluating] = useState(false)
  const evaluationRef = useRef(false)
  const evaluationRequest = useRef<{ order: string; requestId: string }>()
  const currentGame = useRef(gameKey)
  currentGame.current = gameKey
  const evaluationMounted = useRef(true)
  React.useEffect(() => {
    evaluationMounted.current = true
    evaluationRef.current = false
    evaluationRequest.current = undefined
    setEvaluating(false)
    return () => { evaluationMounted.current = false }
  }, [gameKey])
  const [justAddedIds, setJustAddedIds] = useState<string[]>([])
  const [listAlert, setListAlert] = useState<AlertInput>({ open: false })
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackKey, setFeedbackKey] = useState(0)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackTone, setFeedbackTone] = useState<'primary' | 'alt'>('alt')
  const feedbackTimerRef = useRef<number | null>(null)
  const [guessHistoryById, setGuessHistoryById] = useState<Record<string, number[]>>({})
  const [, setHintById] = useState<Record<string, HintDirection>>({})
  const [lastGuessCorrectById, setLastGuessCorrectById] = useState<Record<string, boolean>>({})
  const [lastGuessOrder, setLastGuessOrder] = useState<string[]>([])
  const [result, setResult] = useState<'active' | 'win' | 'loss'>('active')
  const [swapSelectedId, setSwapSelectedId] = useState<string | null>(null)
  const [swapHoveredId, setSwapHoveredId] = useState<string | null>(null)
  const [swapAnimatingIds, setSwapAnimatingIds] = useState<string[]>([])
  const [swapAnimationDirectionById, setSwapAnimationDirectionById] = useState<
    Record<string, 'up' | 'down'>
  >({})
  const [swapPhase, setSwapPhase] = useState<'out' | 'in' | null>(null)
  const [displayGuessItems, setDisplayGuessItems] = useState<SoloItem[]>([])
  const [introEnabled, setIntroEnabled] = useState(false)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [activeDragIndex, setActiveDragIndex] = useState<number>(-1)
  const [suppressDropAnimation, setSuppressDropAnimation] = useState(false)
  const onGuessCountChangeRef = useRef(onGuessCountChange)
  const onStateChangeRef = useRef(onStateChange)
  const appliedResumeRef = useRef<{ gameKey: number; itemsKey: string } | null>(null)
  const skeletonCount = 10

  const showLoading = loading || (!guessItems.length && !displayGuessItems.length)
  const itemsReady = displayGuessItems.length > 0
  const activeItems = displayGuessItems.length
    ? displayGuessItems
    : guessItems.length
      ? guessItems
      : Array.from({ length: skeletonCount }, (_, i) => ({
          id: `loading-${i}`,
          title: 'Loading...',
          index: i + 1,
          points: 1,
          status: 'unchecked' as SoloItem['status'],
        }))
  const unlockedItems = activeItems.filter((item) => !(item as SoloItem).locked)
  const unlockedIds = unlockedItems.map((item) => item.id)
  const lastCountRef = useRef<number>(activeItems.length)

  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((navigator as any).msMaxTouchPoints || 0) > 0
    )
  }, [])
  const useOverlay = isTouchDevice

  const sensors = useSensors(
    ...(isTouchDevice
      ? [useSensor(TouchSensor, { activationConstraint: { distance: 6 } })]
      : [
          useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
          useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
          }),
          useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        ])
  )

  const introKey = useMemo(
    () =>
      items
        .map((item) => getItemId(item))
        .slice()
        .sort()
        .join('|'),
    [items]
  )
  const { visibleCount, isAnimating: isIntroAnimating } = useWaterfallIntro({
    key: introKey,
    count: displayGuessItems.length,
    enabled: introEnabled && displayGuessItems.length > 0,
    onStep: () => sounds?.drag?.(),
  })

  React.useEffect(() => {
    onGuessCountChangeRef.current = onGuessCountChange
  }, [onGuessCountChange])

  React.useEffect(() => {
    onStateChangeRef.current = onStateChange
  }, [onStateChange])

  React.useEffect(() => {
    const itemsKey = items.map((item) => getItemId(item)).join('|')
    if (items.length === 0) {
      setGuessItems([])
      setDisplayGuessItems([])
      setGuessCount(0)
      setJustAddedIds([])
      setFeedbackOpen(false)
      setFeedbackText('')
      setGuessHistoryById({})
      setHintById({})
      setLastGuessCorrectById({})
      setLastGuessOrder([])
      setResult('active')
      setSwapSelectedId(null)
      setSwapHoveredId(null)
      setSwapAnimatingIds([])
      setSwapAnimationDirectionById({})
      setSwapPhase(null)
      onGuessCountChangeRef.current(0)
      onStateChangeRef.current?.(null)
      appliedResumeRef.current = null
      setIntroEnabled(false)
      return
    }
    if (
      resumeState &&
      resumeState.guessItems?.length === items.length &&
      (!appliedResumeRef.current ||
        appliedResumeRef.current.gameKey !== gameKey ||
        appliedResumeRef.current.itemsKey !== itemsKey)
    ) {
      setGuessItems(resumeState.guessItems)
      setDisplayGuessItems(resumeState.guessItems)
      setGuessCount(resumeState.guessCount || 0)
      setJustAddedIds([])
      setFeedbackOpen(false)
      setFeedbackText('')
      setGuessHistoryById(resumeState.guessHistoryById || {})
      setHintById({})
      setLastGuessCorrectById(resumeState.lastGuessCorrectById || {})
      setLastGuessOrder(resumeState.lastGuessOrder || [])
      setResult(resumeState.result || 'active')
      setSwapSelectedId(null)
      setSwapHoveredId(null)
      setSwapAnimatingIds([])
      setSwapAnimationDirectionById({})
      setSwapPhase(null)
      onGuessCountChangeRef.current(resumeState.guessCount || 0)
      appliedResumeRef.current = { gameKey, itemsKey }
      setIntroEnabled(false)
      return
    }
    if (
      appliedResumeRef.current &&
      appliedResumeRef.current.gameKey === gameKey &&
      appliedResumeRef.current.itemsKey === itemsKey
    ) {
      return
    }
    const baseItems = items.map((item) => ({
      ...item,
      id: getItemId(item),
      locked: false,
      status: 'unchecked' as SoloItem['status'],
    }))
    const shuffled = shuffle(baseItems)
    setGuessItems(shuffled)
    setDisplayGuessItems(shuffled)
    setGuessCount(0)
    setJustAddedIds([])
    setFeedbackOpen(false)
    setFeedbackText('')
    setGuessHistoryById({})
    setHintById({})
    setLastGuessCorrectById({})
    setLastGuessOrder([])
    setResult('active')
    setSwapSelectedId(null)
    setSwapHoveredId(null)
    setSwapAnimatingIds([])
    setSwapAnimationDirectionById({})
    setSwapPhase(null)
    onGuessCountChangeRef.current(0)
    appliedResumeRef.current = { gameKey, itemsKey }
    setIntroEnabled(true)
  }, [items, gameKey])

  React.useEffect(() => {
    if (!guessItems.length) return
    onStateChangeRef.current?.({
      guessItems,
      guessCount,
      guessHistoryById,
      lastGuessCorrectById,
      lastGuessOrder,
      result,
    })
  }, [guessItems, guessCount, guessHistoryById, lastGuessCorrectById, lastGuessOrder, result])

  React.useEffect(() => {
    if (!justAddedIds.length) return
    const timer = setTimeout(() => setJustAddedIds([]), 300)
    return () => clearTimeout(timer)
  }, [justAddedIds])

  React.useEffect(() => {
    if (!feedbackOpen) return
    if (feedbackTimerRef.current) {
      window.clearTimeout(feedbackTimerRef.current)
    }
    feedbackTimerRef.current = window.setTimeout(() => {
      setFeedbackOpen(false)
    }, 4000)
    return () => {
      if (feedbackTimerRef.current) {
        window.clearTimeout(feedbackTimerRef.current)
      }
    }
  }, [feedbackOpen])

  React.useEffect(() => {
    if (!swapSelectedId) {
      setSwapHoveredId(null)
    }
  }, [swapSelectedId])

  // if (activeItems.length > 0) {
  //   lastCountRef.current = activeItems.length
  // }

  const reservedListHeight = 10 * (fullScreen ? 66 : 70)
  // useMemo(() => {
  //   return Math.max(lastCountRef.current, 5) * (fullScreen ? 66 : 70)
  // })

  React.useEffect(() => {
    if (!loading && guessItems.length) {
      setDisplayGuessItems(guessItems)
    }
  }, [loading, guessItems])

  const handleDragStart = (event: DragStartEvent) => {
    if (evaluationRef.current) return
    sounds?.drag?.()
    triggerHaptic('light')
  }

  const lastDragOverId = useRef<string | null>(null)
  const lastDragOverAt = useRef<number>(0)

  const handleDragOver = (event: DragOverEvent) => {
    if (result !== 'active') return
    if (!event.over) return
    const overId = String(event.over.id)
    if (overId === lastDragOverId.current) return
    const now = Date.now()
    if (now - lastDragOverAt.current < 80) return
    lastDragOverId.current = overId
    lastDragOverAt.current = now
    sounds?.drag?.()
    triggerHaptic('light')
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    if (result !== 'active') return

    const activeIndex = guessItems.findIndex((item) => item.id === active.id)
    const overIndex = guessItems.findIndex((item) => item.id === over.id)
    if (activeIndex === -1 || overIndex === -1) return

    const activeItem = guessItems[activeIndex]
    const overItem = guessItems[overIndex]
    if (!activeItem || !overItem) return
    if (activeItem.locked || overItem.locked) return

    const lockedPositions = guessItems
      .map((item, idx) => (item.locked ? idx : -1))
      .filter((idx) => idx >= 0)

    const unlocked = guessItems.filter((item) => !item.locked)
    const activeUnlockedIndex = unlocked.findIndex((item) => item.id === active.id)
    const overUnlockedIndex = unlocked.findIndex((item) => item.id === over.id)
    if (activeUnlockedIndex === -1 || overUnlockedIndex === -1) return

    const newUnlocked = arrayMove(unlocked, activeUnlockedIndex, overUnlockedIndex)
    const rebuilt: SoloItem[] = []
    let cursor = 0
    guessItems.forEach((item, idx) => {
      if (lockedPositions.includes(idx)) {
        rebuilt.push(item)
      } else {
        rebuilt.push(newUnlocked[cursor] as SoloItem)
        cursor += 1
      }
    })

    const updated = rebuilt.map((item) => {
      if (item.locked) return item
      return { ...item, status: 'unchecked', justAdded: false }
    })

    setGuessItems(updated as SoloItem[])
    setDisplayGuessItems(updated as SoloItem[])
    sounds?.click?.()
    triggerHaptic('light')
  }

  const handleSwapSelect = (id: string) => {
    if (evaluationRef.current) return
    if (result !== 'active') return
    if (swapAnimatingIds.length) return
    const item = guessItems.find((entry) => entry.id === id)
    if (!item || item.locked) return
    if (!swapSelectedId) {
      setSwapSelectedId(id)
      setSwapHoveredId(null)
      sounds?.click?.()
      triggerHaptic('light')
      return
    }
    if (swapSelectedId === id) {
      setSwapSelectedId(null)
      setSwapHoveredId(null)
      return
    }
    const firstIndex = guessItems.findIndex((entry) => entry.id === swapSelectedId)
    const secondIndex = guessItems.findIndex((entry) => entry.id === id)
    if (firstIndex === -1 || secondIndex === -1) {
      setSwapSelectedId(null)
      setSwapHoveredId(null)
      return
    }
    const firstItem = guessItems[firstIndex]
    const secondItem = guessItems[secondIndex]
    if (firstItem?.locked || secondItem?.locked) {
      setSwapSelectedId(null)
      setSwapHoveredId(null)
      return
    }
    const selectedDirection = secondIndex > firstIndex ? 'down' : 'up'
    const targetDirection = secondIndex > firstIndex ? 'up' : 'down'
    setSwapSelectedId(null)
    setSwapHoveredId(null)
    setSwapAnimatingIds([swapSelectedId, id])
    setSwapAnimationDirectionById({
      [swapSelectedId]: selectedDirection,
      [id]: targetDirection,
    })
    setSwapPhase('out')
    sounds?.drag?.()
    triggerHaptic('medium')
    const next = guessItems.slice()
    if (!next[firstIndex] || !next[secondIndex]) return
    ;[next[firstIndex], next[secondIndex]] = [next[secondIndex], next[firstIndex]]
    const updated = next.map((entry) => {
      if (entry.locked) return entry
      return { ...entry, status: 'unchecked', justAdded: false }
    })
    window.setTimeout(() => {
      setGuessItems(updated as SoloItem[])
      setDisplayGuessItems(updated as SoloItem[])
      setSwapPhase('in')
    }, 160)
    window.setTimeout(() => {
      setSwapAnimatingIds([])
      setSwapAnimationDirectionById({})
      setSwapPhase(null)
    }, 380)
  }

  const handleCheck = async () => {
    if (result !== 'active') return
    if (showLoading || evaluationRef.current) return
    let evaluation: { lockedIds: string[]; guessCount: number } | undefined
    if (evaluateGuess) {
      const key = gameKey
      const order = guessItems.map(item => item.id)
      const signature = JSON.stringify(order)
      if (evaluationRequest.current?.order !== signature) evaluationRequest.current = { order: signature, requestId: globalThis.crypto.randomUUID() }
      evaluationRef.current = true
      setEvaluating(true)
      try {
        evaluation = await evaluateGuess(order, evaluationRequest.current!.requestId)
        if (!evaluationMounted.current || currentGame.current !== key) return
        evaluationRequest.current = undefined
      } catch (error) {
        if (evaluationMounted.current && currentGame.current === key) setListAlert({ open: true, severity: 'error', message: error instanceof Error ? error.message : 'Could not submit this guess. Try again.' })
        return
      } finally {
        if (evaluationMounted.current && currentGame.current === key) {
          evaluationRef.current = false
          setEvaluating(false)
        }
      }
    }
    const nextGuessCount = evaluation?.guessCount ?? guessCount + 1
    const updatedItems = guessItems.map((item, idx) => {
      const position = idx + 1
      const isCorrect = evaluation ? evaluation.lockedIds.includes(item.id) : item.index === position
      const status = isCorrect ? 'correct' : 'unchecked'

      return {
        ...item,
        locked: isCorrect ? true : item.locked,
        status,
      }
    })

    const nextGuessHistory: Record<string, number[]> = { ...guessHistoryById }
    const nextHintById: Record<string, HintDirection> = {}
    const nextGuessCorrect: Record<string, boolean> = {}
    updatedItems.forEach((item, idx) => {
      const position = idx + 1
      nextGuessCorrect[item.id] = item.status === 'correct'
      nextHintById[item.id] =
        item.status === 'correct' ? 'correct' : position < item.index ? 'too-high' : 'too-low'
      if (item.status !== 'correct') {
        const prev = nextGuessHistory[item.id] || []
        if (!prev.includes(position)) {
          nextGuessHistory[item.id] = prev.concat(position)
        }
      }
    })

    const newLastOrder = updatedItems.map((item) => item.id)

    setGuessItems(updatedItems as SoloItem[])
    setDisplayGuessItems(updatedItems as SoloItem[])
    setLastGuessOrder(newLastOrder)
    setGuessHistoryById(nextGuessHistory)
    setHintById(nextHintById)
    setLastGuessCorrectById(nextGuessCorrect)
    setGuessCount(nextGuessCount)
    onGuessCountChangeRef.current(nextGuessCount)
    sounds?.submit?.()

    const correctCount = updatedItems.filter((item) => item.status === 'correct').length
    const prevCorrectCount = guessItems.filter((item) => item.status === 'correct').length
    const newlyCorrect = Math.max(0, correctCount - prevCorrectCount)
    if (nextGuessCount >= maxGuesses || correctCount === updatedItems.length) {
      setFeedbackOpen(false)
    }
    if (correctCount < updatedItems.length && nextGuessCount < maxGuesses) {
      setFeedbackTone(newlyCorrect > 0 ? 'primary' : 'alt')
      if (newlyCorrect > 0) {
        const baseLabel =
          prevCorrectCount === 0
            ? `${newlyCorrect} item${newlyCorrect === 1 ? '' : 's'} ranked!`
            : newlyCorrect === 1
              ? '1 more ranked! Keep it up!'
              : `${newlyCorrect} ranked! Keep it up!`
        setFeedbackText(baseLabel)
      } else {
        setFeedbackText('No new items ranked, try again!')
      }
      setFeedbackOpen(false)
      setFeedbackKey((prev) => prev + 1)
      sounds?.drag?.()
      setTimeout(() => {
        setFeedbackOpen(true)
      }, 10)
      sounds?.miss?.()
    }

    const newlyLocked = updatedItems.some((item, idx) => item.locked && !guessItems[idx]?.locked)
    if (newlyLocked) {
      sounds?.correct?.()
      triggerHaptic('success')
    }

    const allCorrect = updatedItems.every(item => item.status === 'correct')
    if (allCorrect) {
      sounds?.win?.()
      setResult('win')
      onGameOver('win', nextGuessCount, newLastOrder)
      return
    }

    if (nextGuessCount >= maxGuesses) {
      sounds?.loss?.()
      setResult('loss')
      onGameOver('loss', nextGuessCount, newLastOrder)
      triggerHaptic('warning')
      return
    }
    if (!newlyLocked) {
      triggerHaptic('medium')
    }
  }

  const correctOrder = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((item) => ({
          ...item,
          id: getItemId(item),
        })),
    [items]
  )

  const lastGuessPositionById = useMemo(() => {
    const mapping: Record<string, number> = {}
    lastGuessOrder.forEach((id, idx) => {
      mapping[id] = idx + 1
    })
    return mapping
  }, [lastGuessOrder])

  const canCheck = result === 'active' && !evaluating && !showLoading && !isIntroAnimating
  const collisionDetection: CollisionDetection = (args) => {
    const filtered = {
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (container) => !container.data?.current?.locked
      ),
    }
    return closestCenter(filtered)
  }

  return (
    <>
      <Stack
        spacing={1}
        sx={{
          height: fullScreen ? '100%' : 'auto',
          minHeight: 0,
          // overflow: fullScreen ? 'hidden' : 'visible',
        }}
      >
        {result === 'active' ? (
          <>
            <GlassCard
              sx={{
                px: fullScreen ? 1.5 : 1.5,
                py: fullScreen ? 1.5 : 1.5,
              }}
            >
              <Box display="flex" justifyContent="flex-start" alignItems="center">
                <Chip
                  variant="outlined"
                  label={
                    <Box display="flex" gap={0.5} alignItems="center">
                      <HowToVoteRoundedIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                      <Typography variant="caption" fontWeight={700}>
                        {contentType === 'poll' ? 'Poll' : 'Trivia'}
                      </Typography>
                    </Box>
                  }
                  size="small"
                  sx={{
                    backgroundColor: contentType === 'poll' ? altColorLighter : mainColorLighter,
                    borderColor: contentType === 'poll' ? altColor : mainColor,
                    color: contentType === 'poll' ? altColorDarkText : mainColorMediumText,
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="stretch" gap={2}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={0.5}
                  flex={1}
                >
                  {showLoading ? (
                    <Skeleton
                      variant="text"
                      width={fullScreen ? '75%' : '55%'}
                      height={32}
                      sx={{ opacity: 0.6 }}
                      animation="wave"
                    />
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      width="100%"
                      gap={1}
                      sx={{
                        minHeight: 32,
                      }}
                    >
                      <Typography fontWeight={800} fontSize={20} variant="h6" textAlign="left">
                        {title}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Stack justifyContent="center" alignItems="flex-start">
                      <Typography
                        fontSize={fullScreen ? 14 : 16}
                        color="#6b7280"
                        fontWeight={700}
                        textAlign="left"
                      >
                        Guess {guessCount} / {maxGuesses}
                      </Typography>
                      <Typography fontSize={fullScreen ? 14 : 16} color="#94a3b8" fontWeight={700}>
                        {guessCount === 0 ? 'Re-order and submit' : 'Re-order and submit'}
                      </Typography>
                    </Stack>

                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                      justifyContent="space-between"
                      gap={0.75}
                    >
                      <GlassButton
                        variant="contained"
                        onClick={handleCheck}
                        disabled={!canCheck}
                        sx={{
                          px: fullScreen ? 2 : 3,
                          py: fullScreen ? 0.5 : 0.75,
                          fontSize: fullScreen ? 14 : 16,
                          fontWeight: 800,
                          ...(contentType === 'trivia' ? triviaSubmitStyle : {}),
                        }}
                      >
                        Submit ranking
                      </GlassButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </GlassCard>

            <Box
              sx={{
                flex: fullScreen ? 1 : 'unset',
                overflow: fullScreen ? 'visible' : 'visible',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                minHeight: reservedListHeight,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minHeight: !itemsReady ? skeletonCount * 54 : undefined,
                }}
              >
                <Stack
                  spacing={0.66}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    opacity: itemsReady ? 0 : 1,
                    pointerEvents: 'none',
                    transition: 'opacity 300ms ease',
                  }}
                >
                  {Array.from({ length: skeletonCount }).map((_, i) => (
                    <GlassCard
                      key={`solo-skel-${i}`}
                      sx={{
                        px: fullScreen ? 1 : 1.5,
                        py: 1.5,
                        borderRadius: '28px',
                        background: '#ffffff',
                      }}
                    >
                      <Box display="flex">
                        <Skeleton
                          variant="circular"
                          height={30}
                          width={30}
                          animation="wave"
                          sx={{ opacity: 0.6 }}
                        />
                      </Box>
                    </GlassCard>
                  ))}
                </Stack>
                <Box
                  sx={{
                    opacity: itemsReady ? 1 : 0,
                    transition: 'opacity 300ms ease',
                    pointerEvents: itemsReady ? 'auto' : 'none',
                    minHeight: reservedListHeight,
                  }}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={collisionDetection}
                    onDragStart={(event) => {
                      if (useOverlay) {
                        const id = String(event.active.id)
                        setActiveDragId(id)
                        setActiveDragIndex(activeItems.findIndex((item) => item.id === id))
                      }
                      setSuppressDropAnimation(false)
                      handleDragStart(event)
                    }}
                    onDragOver={handleDragOver}
                    onDragEnd={(event) => {
                      if (useOverlay) {
                        setActiveDragId(null)
                        setActiveDragIndex(-1)
                      }
                      setSuppressDropAnimation(true)
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => setSuppressDropAnimation(false))
                      })
                      handleDragEnd(event)
                    }}
                    onDragCancel={() => {
                      if (useOverlay) {
                        setActiveDragId(null)
                        setActiveDragIndex(-1)
                      }
                      setSuppressDropAnimation(true)
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => setSuppressDropAnimation(false))
                      })
                    }}
                    modifiers={[restrictToParentElement]}
                  >
                    <SortableContext items={unlockedIds} strategy={verticalListSortingStrategy}>
                      <Stack spacing={0.66}>
                        {activeItems.map((item, idx) => {
                          const isActiveDrag = useOverlay && activeDragId === item.id
                          const displayIndex = isActiveDrag ? activeDragIndex : idx
                          const selectedIndex = swapSelectedId
                            ? activeItems.findIndex((entry) => entry.id === swapSelectedId)
                            : -1
                          const hoveredIndex = swapHoveredId
                            ? activeItems.findIndex((entry) => entry.id === swapHoveredId)
                            : -1
                          const isSelected = swapSelectedId === item.id
                          const isHovered = swapHoveredId === item.id
                          const swapActive = swapSelectedId !== null
                          const swapIsCancel = isSelected && isHovered
                          const hasHoverTarget =
                            swapHoveredId && hoveredIndex !== -1 && selectedIndex !== -1
                          const selectedDirection =
                            hasHoverTarget && hoveredIndex > selectedIndex ? 'down' : 'up'
                          const hoveredDirection =
                            hasHoverTarget && hoveredIndex > selectedIndex ? 'up' : 'down'
                          const showSwapIcon =
                            swapActive && (isSelected || (isHovered && swapSelectedId !== item.id))
                          const swapIcon = showSwapIcon ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                color: '#087fa2',
                                background: isSelected
                                  ? 'rgba(0, 179, 255,0.1)'
                                  : 'rgba(148,163,184,0.12)',
                              }}
                            >
                              {swapIsCancel ? (
                                <CloseRoundedIcon fontSize="small" />
                              ) : isSelected ? (
                                hasHoverTarget ? (
                                  <ReplyRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      transform:
                                        selectedDirection === 'down'
                                          ? 'scaleX(-1) rotate(-90deg)'
                                          : 'rotate(90deg)',
                                    }}
                                  />
                                ) : (
                                  <SwapVertRoundedIcon fontSize="small" />
                                )
                              ) : hasHoverTarget ? (
                                <ReplyRoundedIcon
                                  fontSize="small"
                                  sx={{
                                    transform:
                                      hoveredDirection === 'down'
                                        ? 'scaleX(-1) rotate(-90deg)'
                                        : 'rotate(90deg)',
                                  }}
                                />
                              ) : (
                                <SwapVertRoundedIcon fontSize="small" />
                              )}
                            </Box>
                          ) : null
                          const isVisible = displayIndex < visibleCount
                          const introOpacity = isVisible ? 1 : 0
                          const introTransform = isVisible ? 'translateY(0px)' : 'translateY(10px)'

                          if ((item as SoloItem).locked) {
                            return (
                              <Box
                                key={item.id}
                                sx={{
                                  pointerEvents: 'none',
                                  opacity: introOpacity,
                                  transform: introTransform,
                                  transition: 'opacity 220ms ease, transform 220ms ease',
                                }}
                              >
                                <SoloItemCard
                                  item={item}
                                  index={idx}
                                  history={guessHistoryById[item.id]}
                                  fullScreen={fullScreen}
                                  isSwapSelected={false}
                                  swapControl={swapIcon || (swapActive ? <Box /> : undefined)}
                                  disabled={isIntroAnimating}
                                  loading={showLoading}
                                />
                              </Box>
                            )
                          }

                          return (
                            <SortableSoloItem
                              key={item.id}
                              item={
                                isActiveDrag
                                  ? ({
                                      ...(item as SoloItem),
                                      isDragPlaceholder: true,
                                    } as SoloItem)
                                  : (item as SoloItem)
                              }
                              index={displayIndex}
                              dragDisabled={
                                evaluating || result !== 'active' || swapSelectedId !== null || isIntroAnimating
                              }
                              clickDisabled={result !== 'active' || isIntroAnimating}
                              isDropDisabled={false}
                              history={guessHistoryById[item.id]}
                              fullScreen={fullScreen}
                              isSwapSelected={swapSelectedId === item.id}
                              isSwapHovered={swapHoveredId === item.id && swapSelectedId !== null}
                              swapAnimationDirection={swapAnimationDirectionById[item.id]}
                              swapPhase={swapPhase}
                              onSwapSelect={() => handleSwapSelect(item.id)}
                              onSwapHover={(id) => {
                                if (swapSelectedId) {
                                  setSwapHoveredId(id)
                                }
                              }}
                              swapControl={
                                swapIcon || (swapActive ? <Box sx={{ width: 28 }} /> : undefined)
                              }
                              isSwapAnimating={swapAnimatingIds.includes(item.id)}
                              swapModeActive={swapSelectedId !== null}
                              loading={showLoading}
                              disabled={isIntroAnimating}
                              introOpacity={introOpacity}
                              introTransform={introTransform}
                              suppressTransition={suppressDropAnimation}
                            />
                          )
                        })}
                        <Box>
                          <GlassButton
                            variant="contained"
                            onClick={handleCheck}
                            disabled={!canCheck}
                            sx={{
                              px: fullScreen ? 2 : 3,
                              py: fullScreen ? 1.5 : 1.75,
                              fontSize: 20,
                              width: '100%',
                              mt: 1,
                              fontWeight: 800,
                              ...(contentType === 'trivia' ? triviaSubmitStyle : {}),
                            }}
                          >
                            Submit ranking
                          </GlassButton>
                        </Box>
                      </Stack>
                    </SortableContext>
                    {useOverlay
                      ? createPortal(
                          <DragOverlay zIndex={2000}>
                            {activeDragId ? (
                              <SoloItemCard
                                item={
                                  activeItems.find((item) => item.id === activeDragId) as SoloItem
                                }
                                index={activeDragIndex >= 0 ? activeDragIndex + 1 : 1}
                                history={guessHistoryById[activeDragId]}
                                fullScreen={fullScreen}
                                loading={showLoading}
                                isDragging
                                isOverlay
                              />
                            ) : null}
                          </DragOverlay>,
                          document.body
                        )
                      : null}
                  </DndContext>
                </Box>
              </Box>
            </Box>
          </>
        ) : null}

        {result !== 'active' ? (
          <GlassCard
            sx={{
              p: {
                xs: 1.5,
                sm: 1.5,
                md: 1.5,
                lg: 2,
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={1}
              mb={1}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
              >
                <Typography variant="h6" fontWeight={800} textAlign="left">
                  Results
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '12px',
                  backgroundColor:
                    lastGuessCorrectById &&
                    Object.values(lastGuessCorrectById).filter(Boolean)
                      .length === items.length
                      ? mainColorLighter
                      : altColorLighter,
                  border: '1px solid',
                  borderColor: result === 'win' ? '#bfd4e3' : '#e8bb99',
                  boxShadow: `0 2px 0 ${result === 'win' ? '#bfd4e3' : '#e8bb99'}`,
                  color:
                    lastGuessCorrectById &&
                    Object.values(lastGuessCorrectById).filter(Boolean)
                      .length === items.length
                      ? mainColorMediumText
                      : altColorDarkText,
                  fontWeight: 800,
                  fontSize: 15,
                }}
              >
                {lastGuessCorrectById &&
                  Object.values(lastGuessCorrectById).filter(Boolean).length}
                /{items.length} Correct
              </Box>
            </Box>
            <Stack spacing={1}>
              {correctOrder.map((item) => {
                const guessedSlot = lastGuessPositionById[item.id]
                const wasCorrect = lastGuessCorrectById[item.id]
                return (
                  <GlassPanel
                    key={`loss-${item.id}`}
                    sx={{
                      p: { xs: 1.25, sm: 1.5 },
                      borderRadius: '18px',
                      background: wasCorrect
                        ? mainColorLighter
                        : altColorLighter,
                      borderColor: wasCorrect ? '#bfd4e3' : '#f1d5be',
                      boxShadow: `0 2px 0 ${wasCorrect ? '#bfd4e3' : '#e8bb99'}`,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="space-between"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        minWidth={0}
                      >
                        <Box
                          sx={{
                            minWidth: 30,
                            height: 30,
                            borderRadius: '10px',
                            flexShrink: 0,
                            background: wasCorrect ? mainColor : altColor,
                            color: wasCorrect
                              ? mainColorMediumText
                              : altColorDarkText,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: 14,
                          }}
                        >
                          {item.index}
                        </Box>
                        {!wasCorrect && guessedSlot ? (
                          <Box
                            sx={{
                              minWidth: 28,
                              flexShrink: 0,
                              height: 28,
                              borderRadius: '999px',
                              border: '1px solid rgba(239,68,68,0.5)',
                              backgroundColor: 'rgba(239,68,68,0.15)',
                              color: '#ef4444',
                              fontWeight: 700,
                              fontSize: 14,
                              borderWidth: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textDecoration: 'line-through',
                              textDecorationColor: '#C01111',
                            }}
                          >
                            {guessedSlot}
                          </Box>
                        ) : null}
                        <Box minWidth={0}>
                          <Typography
                            fontWeight={700}
                            fontSize={16}
                            variant="body1"
                            textAlign="left"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {item.title}
                          </Typography>
                          {item.metric ? (
                            <Typography variant="body2" textAlign="left">
                              {item.metric}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                      <Tooltip title="Open search" arrow>
                        <IconButton
                          size="small"
                          aria-label={`Search for ${item.title}`}
                          sx={{
                            flexShrink: 0,
                            color: wasCorrect
                              ? mainColorMediumText
                              : altColorDarkText,
                          }}
                          onClick={() => {
                            window.open(
                              `https://www.google.com/search?q=${encodeURIComponent(item.title)}`,
                              '_blank',
                            )
                          }}
                        >
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </GlassPanel>
                )
              })}
            </Stack>
          </GlassCard>
        ) : null}
      </Stack>
      <Alert
        key={`feedback-${feedbackKey}`}
        alert={{
          message: feedbackText,
          open: feedbackOpen,
          tone: feedbackTone,
        }}
        duration={3200}
        onClose={() => setFeedbackOpen(false)}
      />
    </>
  )
}
